import React, { createContext, FC, memo, ReactNode, useContext, useEffect, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import moment from 'moment/moment';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { IAvailabilityDataDTO, IUpsertOverrideDataRequestDTO, IUpsertOverrideItemRequestDTO } from 'src/api/dtos';
import AvailabilityRepository from 'src/api/repositories/ess/v1/availability.repository';
import { IError } from 'src/api/types';
import { EmployeeAvailabilitySubmitModal } from 'src/features/availabilities/EmployeeAvailabilitySubmitModal';
import { DATE_TIME_FORMAT } from 'src/shared/constants';
import { AvailabilityStatusEnums, OperationTypeEnums } from 'src/shared/enums';
import { usePrevious } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { getErrorMessage, getSecondsFromTime } from 'src/shared/utils';

const emptyAvailability: IAvailabilityDataDTO = {
  isOverride: false,
  showPreferenceFromRecurringCycleMessage: false,
  availabilities: [],
};

type EmployeeAvailabilitySingleDayProps = {
  isOverride: boolean;
  isRecurring: boolean;
  values: IUpsertOverrideDataRequestDTO;
  touched: FormikTouched<IUpsertOverrideDataRequestDTO>;
  errors: FormikErrors<IUpsertOverrideDataRequestDTO>;
  apiError: string;
  isEditMode: boolean;
  date: moment.Moment | null;
  showSingleDayAvailability: boolean;
  loading: boolean;
  onSetEditMode: () => void;
  onValidateSubmit: () => void;
  onCancel: () => void;
  onChangeDate: (date: moment.Moment | null) => void;
  onChangeFullDay: (wasAllDay: boolean) => void;
  onChangeStatus: (status: AvailabilityStatusEnums, index: number) => void;
  onDeleteOverride: (index: number) => void;
  onAddOverride: () => void;
  onChangeStartTime: (value: string | undefined, index: number) => void;
  onChangeEndTime: (value: string | undefined, index: number) => void;
  onCheckOtherDates: () => void;
  onChangeOtherDate: (date: moment.Moment | null, index: number) => void;
  onDeleteOtherDate: (index: number) => void;
  onAddOtherDate: () => void;
};

const EmployeeAvailabilitySingleDayContext = createContext<EmployeeAvailabilitySingleDayProps>(
  {} as EmployeeAvailabilitySingleDayProps,
);

export const useEmployeeAvailabilitySingleDay = (): EmployeeAvailabilitySingleDayProps =>
  useContext<EmployeeAvailabilitySingleDayProps>(EmployeeAvailabilitySingleDayContext);

export const EmployeeAvailabilitySingleDayProvider: FC<{ children: ReactNode; defaultDate?: string }> = memo(
  ({ defaultDate, children }) => {
    const { t } = useTranslate();

    const defaultMomentDate = defaultDate ? moment(defaultDate) : null;
    const [date, setDate] = useState<moment.Moment | null>(defaultMomentDate);
    const [showSingleDayAvailability, setShowSingleDayAvailability] = useState<boolean>(!!defaultMomentDate);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [openSubmitModal, setOpenSubmitModal] = useState<boolean>(false);
    const [apiError, setApiError] = useState('');

    const {
      currentData: availability,
      isLoading,
      isFetching,
    } = AvailabilityRepository.endpoints.useGetAvailabilityQuery(
      date ? { date: date.format(DATE_TIME_FORMAT) } : skipToken,
    );

    const [updateAvailability, { isLoading: isUpdating }] =
      AvailabilityRepository.endpoints.useUpsertAvailabilityMutation();

    const loading = isLoading || isFetching || isUpdating;

    Yup.setLocale({
      mixed: {
        required: t('global.requiredField'),
      },
    });

    const validationSchema = Yup.object().shape({
      date: Yup.date().required(),
      overrides: Yup.array()
        .of(
          Yup.object({
            status: Yup.mixed<AvailabilityStatusEnums>()
              .test((value) => Object.values(AvailabilityStatusEnums).includes(value as AvailabilityStatusEnums))
              .required(),
            operationType: Yup.mixed<OperationTypeEnums>()
              .test((value) => Object.values(OperationTypeEnums).includes(value as OperationTypeEnums))
              .required(),
            id: Yup.number().optional(),
            startTimeInSeconds: Yup.number().optional(),
            endTimeInSeconds: Yup.number().optional(),
          }).required(),
        )
        .required(),
      otherDates: Yup.array().of(Yup.string().required()).optional(),
    });

    const {
      setFieldValue,
      setValues,
      handleSubmit: handleValidateSubmit,
      values,
      initialValues,
      touched,
      errors,
      resetForm,
    } = useFormik<IUpsertOverrideDataRequestDTO>({
      enableReinitialize: true,
      initialValues: buildInitialPayload(date, availability ?? emptyAvailability),
      validationSchema,
      onSubmit: () => setOpenSubmitModal(true),
    });

    const previousAvailability = usePrevious(availability);

    useEffect(() => {
      if (!date || isEqual(previousAvailability, availability)) {
        return;
      }

      setValues(buildInitialPayload(date, availability ?? emptyAvailability));
    }, [date, availability]);

    /** HANDLERS **/

    const handleSubmit = async () => {
      try {
        await updateAvailability(values).unwrap();

        toast.info(t('availability.youHaveSuccessfullyUpdatedYourAvailabilities'), { type: toast.TYPE.SUCCESS });
        setIsEditMode(false);
        setApiError('');
      } catch (err) {
        const error = err as IError;
        console.error('Submit Error', { ...error });
        const errorMessage = getErrorMessage(error, t('global.oopsAnErrorOccurred'));
        toast.info(errorMessage, { type: toast.TYPE.ERROR });
        setApiError(errorMessage);
      } finally {
        setOpenSubmitModal(false);
      }
    };

    const handleCancel = () => {
      setIsEditMode(false);
      setApiError('');
      resetForm();
    };

    const handleChangeDate = (date: moment.Moment | null) => {
      setDate(date);
      setShowSingleDayAvailability(!!date);
      handleCancel();
    };

    const handleChangeAvailabilityStatus = (status: AvailabilityStatusEnums, index: number) => {
      setValues((prevValues) => ({
        ...prevValues,
        overrides: prevValues.overrides.map((override, currentIndex) => {
          if (index !== currentIndex) {
            return { ...override };
          }

          return {
            ...override,
            status:
              status === AvailabilityStatusEnums.AVAILABLE
                ? AvailabilityStatusEnums.NOT_AVAILABLE
                : AvailabilityStatusEnums.AVAILABLE,
          };
        }),
      }));
    };

    const handleChangeFullDay = (wasFullDay: boolean) => {
      let newOverrides: IUpsertOverrideItemRequestDTO[];

      if (!initialValues.overrides.length) {
        // Doesn't have initial overrides; aka Not Specified
        if (!values.overrides.length) {
          return; // Edge case; values.overrides.length should never be 0 (see handleSetEditMode)
        }

        if (wasFullDay) {
          // Change current override with time ranges
          newOverrides = [{ ...values.overrides[0], startTimeInSeconds: 0, endTimeInSeconds: 0 }];
        } else {
          // Change current override without time ranges
          newOverrides = [{ ...values.overrides[0], startTimeInSeconds: undefined, endTimeInSeconds: undefined }];
        }
      } else {
        // Has initial overrides
        const hasInitialAllDay = !initialValues.overrides.some(
          ({ startTimeInSeconds, endTimeInSeconds }) => !isNil(startTimeInSeconds) || !isNil(endTimeInSeconds),
        );

        // Reset to initial overrides with (initial or 0) time ranges
        if (wasFullDay) {
          newOverrides = initialValues.overrides.map((override) => ({
            ...override,
            startTimeInSeconds: override.startTimeInSeconds ?? 0,
            endTimeInSeconds: override.endTimeInSeconds ?? 0,
          }));
          // Reset to initial overrides without time ranges
        } else {
          newOverrides = hasInitialAllDay
            ? [...initialValues.overrides] // There should only be one override
            : initialValues.overrides.map((override, index) => {
                return {
                  ...override,
                  startTimeInSeconds: undefined,
                  endTimeInSeconds: undefined,
                  operationType: index === 0 ? override.operationType : OperationTypeEnums.DELETE,
                };
              });
        }
      }

      setValues((prevValues) => ({
        ...prevValues,
        overrides: newOverrides,
      }));
    };

    const handleDeleteOverride = (index: number) => {
      // Do not allow deleting last override
      if (values.overrides.filter((override) => override.operationType !== OperationTypeEnums.DELETE).length <= 1) {
        return;
      }

      const newOverrides = values.overrides.reduce<IUpsertOverrideDataRequestDTO['overrides']>(
        (list, currentOverride, currentIndex) => {
          if (index !== currentIndex) {
            // Do nothing
            return [...list, { ...currentOverride }];
          }

          if (currentOverride.operationType === OperationTypeEnums.ADD) {
            // Remove / Don't include that newly added override
            return [...list];
          }

          return [...list, { ...currentOverride, operationType: OperationTypeEnums.DELETE }];
        },
        [],
      );

      setValues((prevValues) => ({
        ...prevValues,
        overrides: newOverrides,
        otherDates:
          !!prevValues.otherDates?.length &&
          newOverrides.some((newOverride) => newOverride.operationType !== OperationTypeEnums.DELETE)
            ? [...prevValues.otherDates]
            : [],
      }));
    };

    const handleAddOverride = () => {
      if (!values.overrides.length) {
        // Create new override (full day, not available by default)
        setValues((prevValues) => ({
          ...prevValues,
          overrides: [
            {
              status: AvailabilityStatusEnums.NOT_AVAILABLE,
              operationType: OperationTypeEnums.ADD,
            },
          ],
        }));
      } else {
        // Create new override based on last override (if time range, set start and end times to 0)
        setValues((prevValues) => {
          const previousOverride = prevValues.overrides[prevValues.overrides.length - 1];

          return {
            ...prevValues,
            overrides: [
              ...prevValues.overrides,
              {
                ...previousOverride,
                id: undefined,
                operationType: OperationTypeEnums.ADD,
                startTimeInSeconds: !isNil(previousOverride.startTimeInSeconds) ? 0 : undefined,
                endTimeInSeconds: !isNil(previousOverride.endTimeInSeconds) ? 0 : undefined,
              },
            ],
          };
        });
      }
    };

    const handleSetEditMode = () => {
      if (!values.overrides.length) {
        handleAddOverride();
      }

      setIsEditMode(true);
    };

    const handleChangeStartTime = (value: string | undefined, index: number) => {
      if (!values.overrides.length) {
        return;
      }

      setFieldValue(`overrides[${index}].startTimeInSeconds`, getSecondsFromTime(value));
    };

    const handleChangeEndTime = (value: string | undefined, index: number) => {
      if (!values.overrides.length) {
        return;
      }

      setFieldValue(`overrides[${index}].endTimeInSeconds`, getSecondsFromTime(value));
    };

    const handleCheckOtherDates = () => {
      const checked = !!values.otherDates?.length;

      setValues((prevValues) => ({
        ...prevValues,
        otherDates: checked ? undefined : [''], // set empty string to add a new date selector for the user to choose from
      }));
    };

    const handleChangeOtherDate = (date: moment.Moment | null, index: number) => {
      if (!date) {
        return;
      }

      setFieldValue(`otherDates[${index}]`, date.format(DATE_TIME_FORMAT));
    };

    const handleAddOtherDates = () => {
      setValues((prevValues) => ({
        ...prevValues,
        otherDates: prevValues.otherDates?.length ? [...prevValues.otherDates, ''] : [''], // set empty string to add a new date selector for the user to choose from
      }));
    };

    const handleDeleteOtherDate = (index: number) => {
      setValues((prevValues) => ({
        ...prevValues,
        otherDates: prevValues.otherDates?.length ? prevValues.otherDates.filter((_, i) => i !== index) : [],
      }));
    };

    const value: EmployeeAvailabilitySingleDayProps = {
      isOverride: availability?.isOverride ?? false,
      isRecurring: availability?.showPreferenceFromRecurringCycleMessage ?? false,
      values,
      touched,
      errors,
      apiError,
      isEditMode,
      date,
      showSingleDayAvailability,
      loading,
      onSetEditMode: handleSetEditMode,
      onValidateSubmit: handleValidateSubmit,
      onCancel: handleCancel,
      onChangeDate: handleChangeDate,
      onChangeFullDay: handleChangeFullDay,
      onChangeStatus: handleChangeAvailabilityStatus,
      onDeleteOverride: handleDeleteOverride,
      onAddOverride: handleAddOverride,
      onChangeStartTime: handleChangeStartTime,
      onChangeEndTime: handleChangeEndTime,
      onCheckOtherDates: handleCheckOtherDates,
      onChangeOtherDate: handleChangeOtherDate,
      onDeleteOtherDate: handleDeleteOtherDate,
      onAddOtherDate: handleAddOtherDates,
    };

    return (
      <EmployeeAvailabilitySingleDayContext.Provider value={value}>
        {children}
        <EmployeeAvailabilitySubmitModal
          dates={[values.date, ...(values.otherDates ?? [])]}
          open={openSubmitModal}
          onClose={() => setOpenSubmitModal(false)}
          onSubmit={handleSubmit}
          loading={loading}
        />
      </EmployeeAvailabilitySingleDayContext.Provider>
    );
  },
);

const buildInitialPayload = (date: moment.Moment | null, data: IAvailabilityDataDTO): IUpsertOverrideDataRequestDTO => {
  return {
    date: (date ?? moment()).format(DATE_TIME_FORMAT),
    overrides: data.availabilities.map(({ overrideId, status, startTimeInSeconds, endTimeInSeconds }) => ({
      status,
      operationType: !isNil(overrideId) ? OperationTypeEnums.UPDATE : OperationTypeEnums.ADD,
      id: overrideId ?? undefined,
      startTimeInSeconds: startTimeInSeconds ?? undefined,
      endTimeInSeconds: endTimeInSeconds ?? undefined,
    })),
    otherDates: [],
  };
};
